import { sumBy } from 'lib/helpers/utility';

export const calcInvoiceDiscountFromProducts = ({
  products,
  procedures,
  hasDiscountInProducts,
  initialInvoice
} = {}) => {
  let amountCents = 0;
  let invAmount = 0;

  const anyItemHasDiscount =
    products?.some((p) => p?.discount) || procedures?.some((s) => s?.discount);

  products?.forEach((product) => {
    let discount = product?.discount;

    // update discount of old versions that had discount only in invoice level
    if (!discount && !hasDiscountInProducts && !anyItemHasDiscount && initialInvoice?.discount) {
      discount = getProductDiscountFromInvoice({
        productCost: product.amount_cents,
        invoice: initialInvoice,
        products,
        procedures
      });
    }

    amountCents += discount?.amount_cents || 0;
    invAmount += product?.total_amount_cents || 0;
  });

  procedures?.forEach((service) => {
    let discount = service?.discount;

    // update discount of old versions that had discount only in invoice level
    if (!discount && !hasDiscountInProducts && !anyItemHasDiscount && initialInvoice?.discount) {
      discount = getProductDiscountFromInvoice({
        productCost: service?.total_cost_cents,
        invoice: initialInvoice,
        products,
        procedures
      });
    }

    amountCents += discount?.amount_cents || 0;
    invAmount += (service?.pt_balance ?? service?.total_cost_cents) || 0;
  });

  return {
    amount_cents: amountCents,
    percentage: Math.round((amountCents * 10000) / invAmount),
    type: 'amount'
  };
};

export const getInvoiceItemDiscountCents = (
  item,
  { initialInvoice = item?.initialInvoice, hasDiscountInProducts } = {}
) => {
  const isPackageWithItems =
    item?.type === 'Package' && (item?.products?.length || item?.procedures?.length);

  if (isPackageWithItems) {
    return (
      calcInvoiceDiscountFromProducts({
        products: item?.products,
        procedures: item?.procedures,
        initialInvoice,
        hasDiscountInProducts
      })?.amount_cents || 0
    );
  }

  return item?.discount?.amount_cents || 0;
};

export const getInvoiceLegacyItemsTotalCents = (invoice) => {
  const sumProceduresCost = (list) =>
    list?.reduce(
      (acc, curr) => acc + (+(curr?.pt_balance ?? curr?.total_cost_cents ?? 0) || 0),
      0
    ) || 0;

  const packages = invoice?.packages || [];

  const productsTotal =
    sumBy(invoice?.products, 'amount_cents') +
    packages.reduce((acc, pack) => acc + sumBy(pack?.products, 'amount_cents'), 0);

  const proceduresTotal =
    sumProceduresCost(invoice?.procedures) +
    packages.reduce((acc, pack) => acc + sumProceduresCost(pack?.procedures), 0);

  return productsTotal + proceduresTotal;
};

export const getProductDiscountFromInvoice = ({
  productCost = 0,
  invoice,
  products,
  procedures
}) => {
  const invoiceDiscountAmount = invoice?.discount;

  let productDiscount = 0;

  if (invoiceDiscountAmount > 0) {
    const productsTotal = sumBy(products, 'amount_cents');

    const proceduresTotal =
      procedures?.reduce((acc, curr) => {
        acc += +(curr.pt_balance ?? curr.total_cost_cents ?? 0) || 0;

        return acc;
      }, 0) || 0;

    const totalCents = invoice?.itemsTotalCents || productsTotal + proceduresTotal;

    if (totalCents > 0) {
      productDiscount = Math.round((productCost / totalCents) * invoiceDiscountAmount) || 0;
    }
  }

  const productPercentage = +((productDiscount / productCost) * 100) || 0;

  return {
    amount_cents: productDiscount,
    percentage: productPercentage,
    type: 'amount'
  };
};

// calc tax for inv
export const calculateInvoiceTaxFromItems = ({
  products,
  procedures,
  packages,
  mergedItems
} = {}) => {
  let taxAmount = 0;

  products?.forEach((item) => {
    if (!item) return;

    const itemAmount = Math.round(item.total_amount_cents || 0);
    const percent = ((item?.local_tax_rate || 0) + (item?.state_tax_rate || 0)) / 10000;

    const netAmount = Math.round(itemAmount - (item?.discount?.amount_cents || 0));
    taxAmount += Math.round(netAmount * percent);
  });

  [...(procedures || []), ...(mergedItems || [])]?.forEach((item) => {
    if (!item) return;

    // check if item is package then recall calculateInvoiceTaxFromItems to calculate amount
    if (item?.products?.length || item?.procedures?.length) {
      taxAmount += calculateInvoiceTaxFromItems({ packages: [item] });
      return;
    }

    const qty =
      !item?.type || item?.type?.toLowerCase() === 'product'
        ? 1
        : (item?.quantity ?? item?.units ?? item?.sales_count) || 1;

    const itemAmount = Math.round(
      item?.pt_balance ?? item?.total_cost_cents ?? item?.total_amount_cents ?? 0
    );

    const discountCents = item?.discount?.amount_cents || 0;

    const netAmount = Math.round(itemAmount * qty - discountCents);

    const percent = ((item?.local_tax_rate || 0) + (item?.state_tax_rate || 0)) / 10000;

    taxAmount += Math.round(netAmount * percent);
  });

  packages?.forEach((item) => {
    if (item?.products?.length || item?.procedures?.length) {
      taxAmount += calculateInvoiceTaxFromItems(item);
    }
  });

  return Math.round(taxAmount);
};

// calc state & local tax amounts separately (mirrors calculateInvoiceTaxFromItems)
export const calculateInvoiceTaxByTypeFromItems = ({
  products,
  procedures,
  packages,
  mergedItems
} = {}) => {
  let localTax = 0;
  let stateTax = 0;

  const addNetTax = (netAmount, item) => {
    const localRate = (item?.local_tax_rate || 0) / 10000;
    const stateRate = (item?.state_tax_rate || 0) / 10000;

    const combined = Math.round(netAmount * (localRate + stateRate));
    const stateShare = Math.round(netAmount * stateRate);

    stateTax += stateShare;
    localTax += combined - stateShare;
  };

  const addNested = (nested) => {
    localTax += nested.localTax;
    stateTax += nested.stateTax;
  };

  products?.forEach((item) => {
    if (!item) return;

    const itemAmount = Math.round(item.total_amount_cents || 0);
    const netAmount = Math.round(itemAmount - (item?.discount?.amount_cents || 0));
    addNetTax(netAmount, item);
  });

  [...(procedures || []), ...(mergedItems || [])]?.forEach((item) => {
    if (!item) return;

    // check if item is a package then recurse to calculate its items' tax
    if (item?.products?.length || item?.procedures?.length) {
      addNested(calculateInvoiceTaxByTypeFromItems({ packages: [item] }));
      return;
    }

    const qty =
      !item?.type || item?.type?.toLowerCase() === 'product'
        ? 1
        : (item?.quantity ?? item?.units ?? item?.sales_count) || 1;

    const itemAmount = Math.round(
      item?.pt_balance ?? item?.total_cost_cents ?? item?.total_amount_cents ?? 0
    );

    const discountCents = item?.discount?.amount_cents || 0;

    const netAmount = Math.round(itemAmount * qty - discountCents);

    addNetTax(netAmount, item);
  });

  packages?.forEach((item) => {
    if (item?.products?.length || item?.procedures?.length) {
      addNested(calculateInvoiceTaxByTypeFromItems(item));
    }
  });

  return { localTax: Math.round(localTax), stateTax: Math.round(stateTax) };
};
